import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import { authSelector } from '../../store/slices/auth';
import { API_KEY, API_CLIENTS_NAME } from '../../utils/constants';
import ReactSelect from 'react-select';
import {
  Search,
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  Download,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AGENTS_API =
  process.env.REACT_APP_SEO_AGENTS_API ||
  'https://e130nezp1b.execute-api.us-east-2.amazonaws.com';

const CACHE_PREFIX = 'seo_audit_v3_cache_';

const getCacheKeyForUrl = (url) =>
  CACHE_PREFIX + (url || '').trim().replace(/[^a-zA-Z0-9]/g, '_');

const SECTION_GROUPS = [
  {
    id: 'basic-seo',
    title: 'Basic SEO',
    indicatorIds: [
      'seo_title',
      'seo_description',
      'common_keywords',
      'keywords_in_title_description',
      'h1_heading',
      'h2_headings',
      'image_alt_attributes',
      'links_ratio',
      'robots_txt',
      'sitemap'
    ]
  },
  {
    id: 'technical-seo',
    title: 'Technical SEO',
    indicatorIds: [
      'mobile_friendly',
      'page_speed',
      'https',
      'canonical',
      'open_graph',
      'structured_data'
    ]
  }
];

const STATUS_CONFIG = {
  Passed: {
    badge: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
    Icon: CheckCircle
  },
  'Needs Work': {
    badge:
      'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    Icon: AlertTriangle
  },
  Failed: {
    badge: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
    Icon: XCircle
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#ef4444';
};

const WebsiteSEOAuditV2 = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const getCustomerList = () => {
    if (!customerList?.length) dispatch(getClients());
    return (customerList || []).map((c) => ({
      ...c,
      label: c.clientName,
      value: c.clientName
    }));
  };

  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;
    let clientData = selectedOption;
    try {
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );
      clientData = response?.data || selectedOption;
    } catch {
      // use selectedOption as fallback
    }
    dispatch(setSelectedClient(clientData));

    // Reset audit state and auto-run for the new client's URL
    const newWebsiteUrl = clientData?.mergeFields?.['~Website'] || '';
    setAuditResults(null);
    setSuggestions({});
    setExpandedIndicator(null);
    setExpandedSections({ 'basic-seo': false, 'technical-seo': false });
    if (newWebsiteUrl) {
      setCustomUrl(newWebsiteUrl);
      runAuditForUrl(newWebsiteUrl);
    }
  };

  const [customUrl, setCustomUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState('');
  const [auditResults, setAuditResults] = useState(null);
  const [error, setError] = useState('');

  const [expandedSections, setExpandedSections] = useState({
    'basic-seo': false,
    'technical-seo': false
  });
  const [expandedIndicator, setExpandedIndicator] = useState(null);

  const [suggestions, setSuggestions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState({});

  const reportRef = useRef(null);

  const websiteUrl = selectedClient?.mergeFields?.['~Website'] || '';

  useEffect(() => {
    if (websiteUrl) setCustomUrl(websiteUrl);
  }, [websiteUrl]);

  // --- Cache helpers ---
  const saveCachedResults = (url, results) => {
    if (!url || !results) return;
    try {
      localStorage.setItem(
        getCacheKeyForUrl(url),
        JSON.stringify({ results, savedAt: new Date().toISOString() })
      );
    } catch {
      // ignore storage errors
    }
  };

  const loadCachedResults = (url) => {
    if (!url) return null;
    try {
      const raw = localStorage.getItem(getCacheKeyForUrl(url));
      if (!raw) return null;
      const { results, savedAt } = JSON.parse(raw);
      const age = Date.now() - new Date(savedAt).getTime();
      if (age > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(getCacheKeyForUrl(url));
        return null;
      }
      return results;
    } catch {
      return null;
    }
  };

  const clearCachedResults = (url) => {
    if (!url) return;
    localStorage.removeItem(getCacheKeyForUrl(url));
  };

  useEffect(() => {
    const clientId =
      selectedClient?.id ||
      selectedClient?.clientId ||
      selectedClient?.clientName ||
      '';
    if (!clientId) return;

    const fetchLatest = async () => {
      try {
        const res = await fetch(AGENTS_API + '/seo/audit/latest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId })
        });
        if (!res.ok) return; // 404 = no previous audit, silently ignore
        const data = await res.json();
        setAuditResults(data);
        if (data.url) setCustomUrl(data.url);
      } catch {
        // silently ignore
      }
    };

    const fetchStoredSuggestions = async () => {
      try {
        const res = await fetch(
          AGENTS_API + '/seo/suggest?clientId=' + encodeURIComponent(clientId)
        );
        if (!res.ok) return; // 404 = no suggestions yet, silently ignore
        const data = await res.json();
        if (!data.indicators?.length) return;
        // Build suggestions map — last occurrence per indicator id wins
        const map = {};
        data.indicators.forEach((ind) => {
          if (ind.id && ind.suggestion) map[ind.id] = ind.suggestion;
        });
        setSuggestions((prev) => ({ ...map, ...prev }));
      } catch {
        // silently ignore
      }
    };

    fetchLatest();
    fetchStoredSuggestions();
  }, [selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- URL validation ---
  const isValidUrl = (string) => {
    if (!string || typeof string !== 'string') return false;
    const trimmed = string.trim();
    if (!trimmed) return false;
    try {
      const urlWithProtocol = trimmed.startsWith('http')
        ? trimmed
        : 'https://' + trimmed;
      new URL(urlWithProtocol);
      return true;
    } catch (_) {
      return false;
    }
  };

  // --- Core audit execution (URL passed directly, no state dependency) ---
  const runAuditForUrl = async (url, forceRescan = false) => {
    if (!forceRescan) {
      const cached = loadCachedResults(url);
      if (cached) {
        setAuditResults(cached);
        setError('');
        return;
      }
    } else {
      clearCachedResults(url);
    }

    setError('');
    setAuditResults(null);
    setSuggestions({});
    setExpandedIndicator(null);
    setIsScanning(true);
    setScanMessage('Analyzing website...');

    try {
      const clientId =
        selectedClient?.id ||
        selectedClient?.clientId ||
        selectedClient?.clientName ||
        '';
      const res = await fetch(AGENTS_API + '/seo/audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url, clientId })
      });

      if (!res.ok) throw new Error('Request failed (' + res.status + ')');

      const data = await res.json();
      setAuditResults(data);
      saveCachedResults(url, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsScanning(false);
      setScanMessage('');
    }
  };

  // --- Start Audit (triggered from the button) ---
  const startAudit = async (forceRescan = false) => {
    const urlToAudit = customUrl.trim();

    if (!urlToAudit) {
      setError('Please enter a website URL to analyze.');
      return;
    }
    if (!isValidUrl(urlToAudit)) {
      setError('Please enter a valid website URL (e.g., example.com)');
      return;
    }

    await runAuditForUrl(urlToAudit, forceRescan);
  };

  // --- AI Suggestion fetch (on-demand) ---
  const fetchSuggestion = async (indicator) => {
    if (suggestions[indicator.id] || loadingSuggestions[indicator.id]) return;
    setLoadingSuggestions((prev) => ({ ...prev, [indicator.id]: true }));
    try {
      const clientId =
        selectedClient?.id ||
        selectedClient?.clientId ||
        selectedClient?.clientName ||
        '';
      const res = await fetch(AGENTS_API + '/seo/suggest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ clientId, indicator })
      });
      if (!res.ok) throw new Error('Suggestion fetch failed');
      const data = await res.json();
      setSuggestions((prev) => ({ ...prev, [indicator.id]: data.suggestion }));
    } catch {
      setSuggestions((prev) => ({
        ...prev,
        [indicator.id]: 'Could not load suggestion. Please try again later.'
      }));
    } finally {
      setLoadingSuggestions((prev) => ({ ...prev, [indicator.id]: false }));
    }
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleIndicator = (indicator) => {
    const isOpening = expandedIndicator !== indicator.id;
    setExpandedIndicator(isOpening ? indicator.id : null);
    if (isOpening) fetchSuggestion(indicator);
  };

  // --- Download Report (PDF) ---
  const handleDownloadReport = async () => {
    if (!auditResults) return;
    try {
      const input = reportRef.current;
      if (!input) return;

      const allSections = {};
      SECTION_GROUPS.forEach((s) => {
        allSections[s.id] = true;
      });
      setExpandedSections(allSections);

      await new Promise((resolve) => setTimeout(resolve, 400));

      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      const pageHeight = 297;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('SEO-Audit-' + (auditResults.target || 'Report') + '.pdf');
      setExpandedSections({ 'basic-seo': true, 'technical-seo': true });
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
  };

  // --- SVG Score Circle ---
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const score = auditResults?.overallScore ?? 0;
  const strokeOffset = circumference - (score / 100) * circumference;
  const scoreColor = getScoreColor(score);

  const getSectionIndicators = (sectionId) => {
    const group = SECTION_GROUPS.find((s) => s.id === sectionId);
    if (!group || !auditResults?.indicators) return [];
    return group.indicatorIds
      .map((id) => auditResults.indicators.find((ind) => ind.id === id))
      .filter(Boolean);
  };

  const formattedDate = auditResults?.createdAt
    ? new Date(auditResults.createdAt).toLocaleString()
    : '';

  return (
    <main className="p-4 sm:p-6 lg:p-10 space-y-4 bg-[#FAFAFA] min-h-screen">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-2">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
            Website SEO Audit
            {selectedClient ? ', ' + selectedClient.clientName : ''}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Analyze and improve your website's search engine optimization
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {isSuperAdmin && (
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
              <ReactSelect
                placeholder="Search"
                options={getCustomerList()}
                onChange={handleChangeClient}
                controlShouldRenderValue={false}
                hideSelectedOptions={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                openMenuOnClick={false}
                openMenuOnFocus={false}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null
                }}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    height: '56px',
                    borderRadius: '0.75rem',
                    borderWidth: '1px',
                    borderColor: state.isFocused ? '#3767A9' : '#e5e7eb',
                    boxShadow: state.isFocused ? '0 0 0 1px #3767A9' : 'none',
                    backgroundColor: '#ffffff',
                    paddingLeft: '2.5rem',
                    fontSize: '1rem',
                    '&:hover': { borderColor: '#3767A9' }
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#9ca3af',
                    fontSize: '1rem'
                  }),
                  input: (provided) => ({
                    ...provided,
                    fontSize: '1rem',
                    color: '#1e293b'
                  }),
                  menu: (provided) => ({
                    ...provided,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    zIndex: 9999,
                    boxShadow:
                      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                    color: state.isFocused ? '#1e293b' : '#64748b',
                    cursor: 'pointer',
                    padding: '12px 16px',
                    fontSize: '1rem'
                  })
                }}
              />
            </div>
          )}
          {auditResults && (
            <Button
              variant="outline"
              onClick={handleDownloadReport}
              className="h-14 px-6 text-base font-semibold rounded-xl border-2 gap-3 w-full sm:w-auto">
              <Download className="h-5 w-5" />
              Download Report
            </Button>
          )}
        </div>
      </div>

      {/* URL Input Card */}
      <Card className="rounded-2xl border shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Website URL
          </label>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <Input
              type="text"
              placeholder="https://example.com"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') startAudit();
              }}
              className="flex-1 h-14 text-base rounded-xl border focus:border-primary px-4"
              disabled={isScanning}
            />
            <Button
              onClick={() => startAudit()}
              disabled={isScanning || !customUrl.trim()}
              className="h-14 px-8 text-base font-semibold rounded-xl gap-2 w-full sm:w-auto text-white"
              style={{ backgroundColor: '#3767A9' }}>
              {isScanning ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5" />
                  Run Audit
                </>
              )}
            </Button>
          </div>
          {error && <p className="text-red-500 mt-3 text-sm">{error}</p>}
          {isScanning && (
            <div className="mt-3 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <p className="text-blue-600 text-sm">{scanMessage}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {auditResults && (
        <div ref={reportRef} className="space-y-4">
          {/* Score Summary Card */}
          <Card className="rounded-2xl border shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                {/* SVG Score Circle */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="relative h-28 w-28 sm:h-32 sm:w-32">
                    <svg
                      className="w-full h-full -rotate-90"
                      viewBox="0 0 120 120">
                      <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        className="fill-none stroke-gray-200"
                        strokeWidth="10"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        className="fill-none transition-all duration-700"
                        stroke={scoreColor}
                        strokeWidth="10"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeOffset}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl sm:text-3xl font-bold text-foreground">
                        {score}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        / 100
                      </span>
                    </div>
                  </div>
                  <span className="text-xs sm:text-sm text-muted-foreground font-medium mt-2">
                    Overall Score
                  </span>
                </div>

                {/* URL + stats + meta */}
                <div className="flex-1 min-w-0 space-y-3">
                  {auditResults.url && (
                    <a
                      href={
                        auditResults.url.startsWith('http')
                          ? auditResults.url
                          : 'https://' + auditResults.url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-mono hover:underline"
                      style={{ color: '#3767A9' }}>
                      <ExternalLink className="h-3.5 w-3.5" />
                      {auditResults.url}
                    </a>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="rounded-xl bg-green-50 px-4 py-3 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {auditResults.summary?.passed ?? 0}
                      </div>
                      <div className="text-xs text-green-700 font-medium">
                        Passed
                      </div>
                    </div>
                    <div className="rounded-xl bg-yellow-50 px-4 py-3 text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {auditResults.summary?.warnings ?? 0}
                      </div>
                      <div className="text-xs text-yellow-700 font-medium">
                        Needs Work
                      </div>
                    </div>
                    <div className="rounded-xl bg-red-50 px-4 py-3 text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {auditResults.summary?.failed ?? 0}
                      </div>
                      <div className="text-xs text-red-700 font-medium">
                        Failed
                      </div>
                    </div>
                  </div>

                  {(auditResults.reportId || formattedDate) && (
                    <p className="text-xs text-muted-foreground">
                      {auditResults.reportId && (
                        <>
                          Report ID:{' '}
                          <span className="font-mono">
                            {auditResults.reportId}
                          </span>
                          {formattedDate && ' · '}
                        </>
                      )}
                      {formattedDate}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sections */}
          {SECTION_GROUPS.map((section) => {
            const indicators = getSectionIndicators(section.id);
            if (!indicators.length) return null;
            const isOpen = expandedSections[section.id];

            return (
              <Card
                key={section.id}
                className="rounded-2xl border shadow-sm overflow-hidden">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-4 sm:px-6 py-4 bg-muted/40 hover:bg-muted/60 transition-colors">
                  <h2 className="text-sm font-semibold text-foreground">
                    {section.title}
                  </h2>
                  <ChevronDown
                    className={
                      'h-4 w-4 text-muted-foreground transition-transform duration-200' +
                      (isOpen ? ' rotate-180' : '')
                    }
                  />
                </button>

                {/* Indicators */}
                {isOpen && (
                  <div className="divide-y divide-border">
                    {indicators.map((indicator) => {
                      const config =
                        STATUS_CONFIG[indicator.status] ||
                        STATUS_CONFIG['Needs Work'];
                      const StatusIcon = config.Icon;
                      const isIndicatorOpen =
                        expandedIndicator === indicator.id;

                      return (
                        <div key={indicator.id}>
                          <button
                            onClick={() => toggleIndicator(indicator)}
                            className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-muted/30 transition-colors text-left">
                            <div className="w-full sm:w-1/4 shrink-0">
                              <h3 className="font-semibold text-foreground text-sm">
                                {indicator.name}
                              </h3>
                            </div>
                            <div className="hidden sm:block flex-1 min-w-0">
                              <p className="text-sm text-muted-foreground truncate">
                                {indicator.details?.summary}
                              </p>
                            </div>
                            <Badge
                              className={
                                config.badge +
                                ' text-xs px-2.5 py-1 shrink-0 border gap-1 font-medium rounded-full'
                              }>
                              <StatusIcon className="h-3 w-3" />
                              {indicator.status}
                            </Badge>
                            <ChevronDown
                              className={
                                'h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200' +
                                (isIndicatorOpen ? ' rotate-180' : '')
                              }
                            />
                          </button>

                          {isIndicatorOpen && (
                            <div className="px-4 sm:px-6 pb-5 space-y-3">
                              <p className="text-sm text-muted-foreground sm:hidden">
                                {indicator.details?.summary}
                              </p>

                              {indicator.details?.values?.length > 0 && (
                                <div className="rounded-lg bg-foreground text-background px-4 py-3 text-sm overflow-x-auto">
                                  <div className="space-y-1">
                                    {indicator.details.values.map((v, i) => (
                                      <div
                                        key={i}
                                        className="font-mono text-xs break-all">
                                        {v}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {indicator.status !== 'Passed' &&
                                indicator.details?.pages?.length > 0 && (
                                  <div className="rounded-lg border border-red-100 bg-red-50/50 px-4 py-3">
                                    <h4 className="text-xs font-semibold text-red-800 mb-2">
                                      Affected Pages
                                    </h4>
                                    <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                                      {indicator.details.pages.map(
                                        (pageUrl, idx) => (
                                          <li
                                            key={idx}
                                            className="flex items-start gap-2 text-xs text-red-700/80">
                                            <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400" />
                                            <a
                                              href={pageUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hover:underline break-all">
                                              {pageUrl}
                                            </a>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                    {indicator.details.pages.length >= 10 && (
                                      <p className="text-xs text-red-600/70 mt-3 italic">
                                        *Showing up to 10 affected pages
                                      </p>
                                    )}
                                  </div>
                                )}

                              <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3">
                                <div className="flex items-center gap-2 text-yellow-700 text-xs font-semibold mb-1.5">
                                  <Sparkles className="h-3.5 w-3.5" />
                                  AI Suggestion
                                </div>
                                {loadingSuggestions[indicator.id] ? (
                                  <div className="flex items-center gap-2 text-yellow-700 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading suggestion...
                                  </div>
                                ) : suggestions[indicator.id] ? (
                                  <p className="text-sm text-foreground/80 leading-relaxed">
                                    {suggestions[indicator.id]}
                                  </p>
                                ) : (
                                  <p className="text-sm text-yellow-700/60 italic">
                                    No suggestion available.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </main>
  );
};

export default WebsiteSEOAuditV2;
